import { lazy, Suspense, type ReactNode } from 'react';
import { Monitor, Minus, Plus, Scan } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { EngineCapabilities, ScrollInput, ViewState } from '../../viewer/ViewportEngine';
import { OBJECTIVE_SCALE } from '../../viewer/zoomLimits';
import type { ScanSession } from '../../viewer/scan/session';
import { CTRL } from './ctrl';
import { PendingScanBar } from './ReviewScanBarParts';
import { LENS_WIDTH, MagnificationLens } from './MagnificationLens';
import { OverviewMinimap } from './OverviewMinimap';
import { computeScaleBar } from './scaleBar';

// The bar lives in the scan's chunk; by the time a session exists the chunk is
// loaded, so the fallback is never seen.
const ReviewScanBar = lazy(() => import('./reviewScanModule').then((m) => ({ default: m.ReviewScanBar })));

interface Props {
  viewState: ViewState | null;
  capabilities: EngineCapabilities;
  isFullscreen: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFit: () => void;
  onToggleFullscreen: () => void;
  onSetMagnification: (m: number) => void;
  onPreviewAt: (m: number) => Promise<string | null>;
  onGetThumbnail: () => Promise<{ url: string; aspect: number } | null>;
  onNavigate: (x: number, y: number, animate: boolean) => void;
  onZoomAt: (factor: number, x: number, y: number) => void;
  onScrollAt: (e: ScrollInput, x: number, y: number) => void;
  /** The open review scan, if any: the bar at the bottom centre and the
   *  plan and coverage on the minimap. */
  scan?: ScanSession | null;
  /** A review scan whose code is still loading: the bar shows, analyzing. */
  scanPending?: boolean;
  /** The image is shown inverted (an X-ray's Invert): the minimap follows. */
  inverted?: boolean;
}

const HALF = (LENS_WIDTH - 4) / 2;

export function ViewportControls({
  viewState,
  capabilities,
  isFullscreen,
  onZoomIn,
  onZoomOut,
  onFit,
  onToggleFullscreen,
  onSetMagnification,
  onPreviewAt,
  onGetThumbnail,
  onNavigate,
  onZoomAt,
  onScrollAt,
  scan = null,
  scanPending = false,
  inverted = false,
}: Props) {
  const scale = viewState?.mmPerPixel ? computeScaleBar(viewState.mmPerPixel) : null;
  const fsLabel = `${isFullscreen ? 'Exit' : 'Enter'} fullscreen (F)`;
  const FsIcon = Monitor;
  const lensMode = capabilities.magnification;

  return (
    <>
      {/* Top-left: overview minimap (rotate/fullscreen live in the study header). */}
      {lensMode && (
        <div className="pointer-events-none absolute left-2 top-2 z-[5]">
          <OverviewMinimap
            getThumbnail={onGetThumbnail}
            viewRect={viewState?.viewRect ?? null}
            rotationDeg={viewState?.rotationDeg ?? 0}
            onNavigate={onNavigate}
            onZoomAt={onZoomAt}
            onScrollAt={onScrollAt}
            scan={scan?.controller ?? null}
            inverted={inverted}
          />
        </div>
      )}

      {/* Bottom-left: scale bar (+ full controls for non-lens engines) */}
      <div className="pointer-events-none absolute bottom-2 left-2 z-[5] flex flex-col items-start gap-1.5">
        {!lensMode && (
          <>
            {viewState && (
              <div className="rounded-md bg-black/60 px-2 py-0.5 text-xs font-semibold text-white">
                {viewState.zoomPercent}%
              </div>
            )}
            <Btn label="Zoom in (+)" onClick={onZoomIn} disabled={!viewState?.canZoomIn} className="h-9 w-9">
              <Plus className="h-4 w-4" />
            </Btn>
            <Btn label="Zoom out (−)" onClick={onZoomOut} disabled={!viewState?.canZoomOut} className="h-9 w-9">
              <Minus className="h-4 w-4" />
            </Btn>
            <Btn label="Fit to screen (1)" onClick={onFit} className="h-9 w-9">
              <Scan className="h-4 w-4" />
            </Btn>
            <Btn label={fsLabel} onClick={onToggleFullscreen} className="h-9 w-9">
              <FsIcon className="h-4 w-4" />
            </Btn>
          </>
        )}
        {scale && (
          <div className="rounded-md border border-white/15 bg-black/70 px-2 py-1">
            <div className="text-center text-[10px] leading-tight text-white">{scale.label}</div>
            <div
              className="border-x-2 border-b-2 border-white"
              style={{ width: scale.px, height: 5 }}
            />
          </div>
        )}
      </div>

      {/* Bottom centre: the review scan's player. 150 px clear on each side
          for the scale bar and the lens unit; shrinks on narrow panes. After
          the scale bar in the DOM so it paints over it on a phone, before the
          lens unit so the lens and its preview stay on top. */}
      {(scan || scanPending) && (
        <div className="pointer-events-none absolute bottom-2 left-1/2 z-[5] flex w-[calc(100%-300px)] min-w-[240px] max-w-[640px] -translate-x-1/2 justify-center">
          {!scan ? (
            <PendingScanBar />
          ) : (
          <Suspense fallback={<PendingScanBar />}>
            <ReviewScanBar
              scan={scan.controller}
              onClose={scan.close}
              blocked={scan.blocked}
              area={scan.area}
              onArea={scan.setArea}
              tissueOnly={scan.tissueOnly}
              onTissueOnly={scan.setTissueOnly}
              sensitivity={scan.sensitivity}
              onSensitivity={scan.setSensitivity}
              tissue={scan.tissue}
              onImage={scan.onImage}
              onOnImage={scan.setOnImage}
              areas={scan.areas}
              onClearAreas={scan.clearAreas}
            />
          </Suspense>
          )}
        </div>
      )}

      {/* Bottom-right: the magnification lens unit (zoom in / lens / zoom out + fit) */}
      {lensMode && (
        <div className="pointer-events-none absolute bottom-2 right-2 z-[5] flex flex-col items-center gap-1">
          <Btn
            label="Zoom in (+)"
            onClick={onZoomIn}
            disabled={!viewState?.canZoomIn}
            style={{ width: LENS_WIDTH, height: 28 }}
          >
            <Plus className="h-4 w-4" />
          </Btn>
          <MagnificationLens
            magnification={viewState?.magnification ?? null}
            onSelect={onSetMagnification}
            previewAt={onPreviewAt}
            scale={capabilities.zoomScale ?? OBJECTIVE_SCALE}
            previews={capabilities.pictureTiles !== false}
          />
          <div className="pointer-events-auto flex gap-1">
            <Btn label="Zoom out (−)" onClick={onZoomOut} disabled={!viewState?.canZoomOut} style={{ width: HALF, height: 28 }}>
              <Minus className="h-4 w-4" />
            </Btn>
            <Btn label="Fit to screen (1)" onClick={onFit} style={{ width: HALF, height: 28 }}>
              <Scan className="h-4 w-4" />
            </Btn>
          </div>
        </div>
      )}
    </>
  );
}

function Btn({
  label,
  onClick,
  disabled,
  className,
  style,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" onClick={onClick} disabled={disabled} style={style} className={cn(CTRL, className)}>
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="left">{label}</TooltipContent>
    </Tooltip>
  );
}
